import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@lifeomic/chroma-react/components/Button';
import {
  Tab,
  Tabs,
  TabList,
  TabPanel,
} from '@lifeomic/chroma-react/components/Tabs';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LexicalEditor } from 'lexical';
import { defineMessages, useIntl } from 'react-intl';
import moment from 'moment';
import { useNotify } from '../../../hooks';
import { Annotation, AnnotationData } from '../../TextAnnotation/types';
import { useSuggestAgent } from './AITools/ImpressionEditor/useSuggestAgent';
import { removeAnnotationMarks } from './AITools/ImpressionEditor/MarkdownEditor/plugins/AnnotationPlugin';
import { AnnotationsList } from './AnnotationsList';
import { GenerateDrawer } from './GenerateDrawer';

type DrawerTab = 'regenerate' | 'suggestions';

export const suggestionsDrawerMessages = defineMessages({
  tabRegenerate: {
    id: 'suggestionsDrawer.tab.regenerate',
    defaultMessage: 'Regenerate',
  },
  tabSuggestions: {
    id: 'suggestionsDrawer.tab.suggestions',
    defaultMessage: 'Requested Edits',
  },
  suggestionsDescription: {
    id: 'suggestionsDrawer.suggestions.description',
    defaultMessage:
      'Highlight sections in the editor and leave comments for Zori to address.',
  },
  sendSuggestions: {
    id: 'suggestionsDrawer.suggestions.send',
    defaultMessage: 'Request edits',
  },
  suggestInstructions: {
    id: 'suggestionsDrawer.suggestions.instructions',
    defaultMessage: 'Additional instructions (optional)',
  },
  suggestInstructionsPlaceholder: {
    id: 'suggestionsDrawer.suggestions.instructionsPlaceholder',
    defaultMessage:
      'Enter any additional instructions for the requested edits.',
  },
  generationMode: {
    id: 'suggestionsDrawer.tabs.ariaLabel',
    defaultMessage: 'Generation mode',
  },
  suggestConflictNotice: {
    id: 'suggestionsDrawer.suggestions.conflict',
    defaultMessage:
      "Another request is already in progress for this report. We'll update it when that request finishes.",
  },
  suggestInProgressBanner: {
    id: 'suggestionsDrawer.suggestions.inProgressBanner',
    defaultMessage:
      'Zori is reading your feedback and applying requested edits. Please hold…',
  },
  rationaleHeading: {
    id: 'suggestionsDrawer.suggestions.rationaleHeading',
    defaultMessage: "Zori's response",
  },
  noEditsHeading: {
    id: 'suggestionsDrawer.suggestions.noEditsHeading',
    defaultMessage:
      "The last requested edits did not surface any changes. Here's Zori's response:",
  },
  rationaleExpand: {
    id: 'suggestionsDrawer.rationale.expand',
    defaultMessage: 'expand',
  },
  rationaleCollapse: {
    id: 'suggestionsDrawer.rationale.collapse',
    defaultMessage: 'collapse',
  },
});

type SuggestionRationale = {
  text: string;
  generatedAt: string;
};

const RATIONALE_PREVIEW_CHARS = 120;

const inlineButtonSx = {
  display: 'inline',
  background: 'none',
  border: 'none',
  p: 0,
  fontWeight: 'bold',
  color: 'primary.main',
  cursor: 'pointer',
  fontSize: 'inherit',
  fontFamily: 'inherit',
  lineHeight: 'inherit',
  '&:hover': { textDecoration: 'underline' },
};

const RationaleCallout: React.FC<{
  heading: string;
  rationale: SuggestionRationale;
}> = ({ heading, rationale }) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const preview = rationale.text.slice(0, RATIONALE_PREVIEW_CHARS).trimEnd();
  const isTruncated = rationale.text.length > RATIONALE_PREVIEW_CHARS;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: '0.375rem',
        border: theme => `1px solid ${theme.palette.divider}`,
        borderLeft: theme => `3px solid ${theme.palette.primary.main}`,
        boxShadow: 1,
        px: 1.5,
        py: 1.25,
        '& p': { m: 0, fontSize: '0.8125rem', lineHeight: 1.5 },
        '& p + p': { mt: 1 },
        '& ul, & ol': { my: 0.5, pl: 2.5, fontSize: '0.8125rem' },
        '& code': {
          px: 0.5,
          borderRadius: 0.5,
          bgcolor: 'action.hover',
          fontSize: '0.875em',
        },
      }}
    >
      <Box sx={{ fontSize: '0.8125rem', fontWeight: 'bold', lineHeight: 1.5 }}>
        {heading}
      </Box>
      <Box sx={{ mt: 0.5 }}>
        {!isTruncated ? (
          <>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {rationale.text}
            </ReactMarkdown>
            {moment(rationale.generatedAt).isValid() && (
              <Box sx={{ mt: 1, fontSize: '0.8125rem' }}>
                <Text size="caption">
                  {moment(rationale.generatedAt).fromNow()}
                </Text>
              </Box>
            )}
          </>
        ) : !isOpen ? (
          <Box component="p" sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}>
            {preview}
            {'... '}
            <Box
              component="button"
              onClick={() => setIsOpen(true)}
              sx={inlineButtonSx}
            >
              {formatMessage(suggestionsDrawerMessages.rationaleExpand)}
            </Box>
          </Box>
        ) : (
          <>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {rationale.text}
            </ReactMarkdown>
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.8125rem',
              }}
            >
              <Box>
                {moment(rationale.generatedAt).isValid() && (
                  <Text size="caption">
                    {moment(rationale.generatedAt).fromNow()}
                  </Text>
                )}
              </Box>
              <Box
                component="button"
                onClick={() => setIsOpen(false)}
                sx={inlineButtonSx}
              >
                {formatMessage(suggestionsDrawerMessages.rationaleCollapse)}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

type SuggestionsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;

  impressionId: string;
  patientId: string;
  summary: string;

  annotations: Annotation[];
  onAnnotationsChange: (annotations: Annotation[]) => void;
  editorRef: React.RefObject<LexicalEditor | null>;

  onSuccess: () => void;
  onFailureMessage: string;
  onSuggestingChange?: (isSuggesting: boolean) => void;
  onBeforeInvoke?: () => void;

  regenerateTab: React.ReactNode;
  regenerateAction: React.ReactNode;

  showSuggestionsTab?: boolean;
  /** Rationale from the last suggest-agent run, if any. */
  rationale?: SuggestionRationale;
  /** True when a diff is pending review; locks the suggestions tab to read-only. */
  hasPendingDiff?: boolean;
};

export const SuggestionsDrawer: React.FC<SuggestionsDrawerProps> = ({
  isOpen,
  onClose,
  title,
  impressionId,
  patientId,
  summary,
  annotations,
  onAnnotationsChange,
  editorRef,
  onSuccess,
  onFailureMessage,
  onSuggestingChange,
  regenerateTab,
  regenerateAction,
  showSuggestionsTab = true,
  onBeforeInvoke,
  rationale,
  hasPendingDiff = false,
}) => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();

  const [activeTab, setActiveTab] = useState<DrawerTab>('regenerate');
  const [suggestInstructions, setSuggestInstructions] = useState('');

  // Declared early so the impressionId reset effect can sync it.
  const prevRationaleTimestamp = useRef(rationale?.generatedAt);

  useEffect(() => {
    setActiveTab('regenerate');
    setSuggestInstructions('');
    // Sync the ref so the rationale auto-switch effect doesn't misfire when
    // navigating to a different impression that already has a rationale.
    prevRationaleTimestamp.current = rationale?.generatedAt;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impressionId]);

  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;

  // When a suggest run succeeds we clear annotations programmatically.
  // Without this guard the prevAnnotationCount effect would immediately flip
  // the tab back to 'regenerate' before the rationale auto-switch can fire.
  const suppressAnnotationTabSwitch = useRef(false);

  const prevAnnotationCount = useRef(annotations.length);
  useEffect(() => {
    if (prevAnnotationCount.current === 0 && annotations.length > 0) {
      setActiveTab('suggestions');
    } else if (
      !suppressAnnotationTabSwitch.current &&
      prevAnnotationCount.current > 0 &&
      annotations.length === 0
    ) {
      setActiveTab('regenerate');
    }
    suppressAnnotationTabSwitch.current = false;
    prevAnnotationCount.current = annotations.length;
  }, [annotations.length]);

  const { invokeSuggestion, isSuggesting } = useSuggestAgent({
    impressionId,
    patientId,
    onSuccess: () => {
      const editor = editorRef.current;
      if (editor) {
        for (const ann of annotationsRef.current) {
          removeAnnotationMarks(editor, ann.id);
        }
      }
      suppressAnnotationTabSwitch.current = true;
      onAnnotationsChange([]);
      setSuggestInstructions('');
      onSuccess();
      // Keep drawer open so the rationale is immediately visible.
    },
    onFailure: () => {
      notify(onFailureMessage, 'error', 5000);
    },
    onConflict: () => {
      notify(
        formatMessage(suggestionsDrawerMessages.suggestConflictNotice),
        'warning',
        6000,
      );
    },
    onSuggestingChange,
  });

  // Auto-switch to the suggestions tab when a new rationale arrives.
  useEffect(() => {
    if (
      rationale?.generatedAt &&
      rationale.generatedAt !== prevRationaleTimestamp.current &&
      showSuggestionsTab
    ) {
      setActiveTab('suggestions');
    }
    prevRationaleTimestamp.current = rationale?.generatedAt;
  }, [rationale?.generatedAt, showSuggestionsTab]);

  const handleSendSuggestions = useCallback(async () => {
    onBeforeInvoke?.();
    try {
      await invokeSuggestion(
        suggestInstructions,
        annotations.map(
          (a): AnnotationData => ({
            selectedText: a.selectedText,
            comment: a.comment,
          }),
        ),
        summary,
      );
    } catch {
      notify(onFailureMessage, 'error', 5000);
    }
  }, [
    onBeforeInvoke,
    invokeSuggestion,
    suggestInstructions,
    annotations,
    summary,
    notify,
    onFailureMessage,
  ]);

  const suggestionsTabLocked = hasPendingDiff && !!rationale;

  const footerActions =
    activeTab === 'suggestions' && showSuggestionsTab ? (
      suggestionsTabLocked ? null : (
        <Button
          key="send-suggestions"
          onClick={handleSendSuggestions}
          disabled={
            isSuggesting ||
            (annotations.length === 0 && !suggestInstructions.trim())
          }
        >
          {formatMessage(suggestionsDrawerMessages.sendSuggestions)}
        </Button>
      )
    ) : (
      regenerateAction
    );

  return (
    <GenerateDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      actions={footerActions}
    >
      {showSuggestionsTab ? (
        <Tabs selectedTabId={activeTab} variant="pill" manualControl>
          <Box
            sx={{
              flexShrink: 0,
              borderBottom: 1,
              borderColor: 'divider',
              pb: 1.5,
              mb: 1.5,
            }}
          >
            <TabList
              aria-label={formatMessage(
                suggestionsDrawerMessages.generationMode,
              )}
            >
              <Tab
                stopId="regenerate"
                onClick={() => setActiveTab('regenerate')}
              >
                {formatMessage(suggestionsDrawerMessages.tabRegenerate)}
              </Tab>
              <Tab
                stopId="suggestions"
                onClick={() => setActiveTab('suggestions')}
              >
                {formatMessage(suggestionsDrawerMessages.tabSuggestions)}
              </Tab>
            </TabList>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <TabPanel stopId="regenerate">{regenerateTab}</TabPanel>
            <TabPanel stopId="suggestions">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  pt: 1,
                }}
              >
                {suggestionsTabLocked && rationale ? (
                  <RationaleCallout
                    key={rationale.generatedAt}
                    heading={formatMessage(
                      suggestionsDrawerMessages.rationaleHeading,
                    )}
                    rationale={rationale}
                  />
                ) : (
                  <>
                    {isSuggesting ? (
                      <Box
                        role="status"
                        aria-live="polite"
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          borderLeft: 3,
                          borderColor: 'primary.main',
                        }}
                      >
                        <Text size="subbody" weight="bold">
                          {formatMessage(
                            suggestionsDrawerMessages.suggestInProgressBanner,
                          )}
                        </Text>
                      </Box>
                    ) : rationale ? (
                      <RationaleCallout
                        key={rationale.generatedAt}
                        heading={formatMessage(
                          suggestionsDrawerMessages.noEditsHeading,
                        )}
                        rationale={rationale}
                      />
                    ) : (
                      <Text size="subbody">
                        {formatMessage(
                          suggestionsDrawerMessages.suggestionsDescription,
                        )}
                      </Text>
                    )}
                    <AnnotationsList
                      annotations={annotations}
                      onAnnotationsChange={onAnnotationsChange}
                      editorRef={editorRef}
                    />
                    <TextArea
                      style={{ resize: 'none' }}
                      label={formatMessage(
                        suggestionsDrawerMessages.suggestInstructions,
                      )}
                      placeholder={formatMessage(
                        suggestionsDrawerMessages.suggestInstructionsPlaceholder,
                      )}
                      value={suggestInstructions}
                      onChange={e => setSuggestInstructions(e.target.value)}
                      fullWidth
                      rows={4}
                    />
                  </>
                )}
              </Box>
            </TabPanel>
          </Box>
        </Tabs>
      ) : (
        regenerateTab
      )}
    </GenerateDrawer>
  );
};
